import { useCallback, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";
import { Box, Icon, MenuControlled } from "../../../common/components";
import Avatar from "../../../common/components/Avatar/Avatar";
import { type MenuOption } from "../../../common/components/Menu/MenuControlled";
import { useAuth } from "../../../common/hooks/useAuth";
import { useSelector } from "../../../common/hooks/useSelector";
import { createStyles, palette } from "../../../styles/theme";
import { ROUTES } from "../../../constant/common.constant";
import { getInitials } from "../../../utils/initialsUtils";
import { isProd } from "../../../utils/env";
import RageQuitModal from "../../RageQuitModal";

const styles = createStyles({
  container: {
    cursor: "pointer",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    px: 1,
    "&:hover": {
      background: palette.brand.paleEffect,
    },
  },
  selectableMenuItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
});

const ProfileMenu = () => {
  const profileRef = useRef<HTMLDivElement>(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isRageQuitModalOpen, setIsRageQuitModalOpen] = useState(false);
  const userInfos = useSelector((state) => state.userInfo);

  const navigate = useNavigate();

  const { logout } = useAuth();

  const goToOrders = useCallback(() => {
    navigate(ROUTES.ORDERS);
  }, [navigate]);

  const goToAllOrders = useCallback(() => {
    navigate(ROUTES.ADMIN_ALL_ORDERS);
  }, [navigate]);

  const goToDistributorList = useCallback(() => {
    navigate(ROUTES.ADMIN_DISTRIBUTORS);
  }, [navigate]);

  const goToUserRoles = useCallback(() => {
    navigate(ROUTES.ADMIN_USERS);
  }, [navigate]);

  const goToSpecsEditor = useCallback(() => {
    navigate(ROUTES.SPECS_GENERATOR);
  }, [navigate]);
  const goToFamiliesEditor = useCallback(() => {
    navigate(ROUTES.SPECS_FAMILIES);
  }, [navigate]);
  const goToFabricsEditor = useCallback(() => {
    navigate(ROUTES.SPECS_FABRICS);
  }, [navigate]);
  const goToDecorationsEditor = useCallback(() => {
    navigate(ROUTES.SPECS_DECORATIONS);
  }, [navigate]);

  const goToProfile = useCallback(() => {
    navigate(ROUTES.USER_PROFILE);
  }, [navigate]);

  const openRageQuitModal = useCallback(() => {
    setIsRageQuitModalOpen(true);
  }, []);

  const options: MenuOption[] = [
    {
      labelKey: "profile.all_orders",
      onClick: goToAllOrders,
      adminOnly: true,
    },
    {
      labelKey: "profile.manage_distributors",
      onClick: goToDistributorList,
      adminOnly: true,
    },
    ...(userInfos.isAdmin
      ? [
          {
            label: "Manage user roles",
            onClick: goToUserRoles,
            adminOnly: true,
          },
        ]
      : []),
    ...(isProd
      ? []
      : [
          {
            labelKey: "profile.specs_generator",
            onClick: goToSpecsEditor,
            adminOnly: true,
          },
          {
            label: "Families",
            onClick: goToFamiliesEditor,
            adminOnly: true,
          },
          {
            label: "Fabrics",
            onClick: goToFabricsEditor,
            adminOnly: true,
          },
          {
            label: "Decorations",
            onClick: goToDecorationsEditor,
            adminOnly: true,
          },
        ]),
    {
      labelKey: "profile.rage_quit",
      onClick: openRageQuitModal,
      adminOnly: true,
    },
    { isDivider: true, adminOnly: true },
    {
      labelKey: "profile.my_profile",
      onClick: goToProfile,
    },
    {
      labelKey: "profile.my_orders",
      onClick: goToOrders,
    },
    { isDivider: true },
    {
      labelKey: "general.logout",
      onClick: logout,
    },
  ];

  return (
    <>
      <Box
        ref={profileRef}
        onClick={() => setIsProfileMenuOpen((prev) => !prev)}
        sx={styles.container}
        data-test="userProfile.toggle"
      >
        <Avatar
          userId={userInfos.id}
          imageUrl={userInfos.imageUrl}
          initials={getInitials(userInfos.firstName, userInfos.lastName)}
          size="md"
        />
        <Icon icon={"expand_more"} size={16} color={palette.grey.nearBlack} />
      </Box>
      <MenuControlled
        options={options}
        isOpen={isProfileMenuOpen}
        onClose={() => setIsProfileMenuOpen(false)}
        target={profileRef.current}
      />
      <RageQuitModal
        open={isRageQuitModalOpen}
        onClose={() => setIsRageQuitModalOpen(false)}
      />
    </>
  );
};

export default ProfileMenu;
